// Intake view (file-first).
//
// Shown until the user clicks "Continue to chat". Single column, upload
// front-and-centre. We surface a live extraction status so the user knows the
// AI is still working through their files before they move on.

import { useMarkIntakeDone } from '../cases';
import { useFiles } from '../files';
import { m } from '../paraglide/messages';
import { FilesPanel } from './files-panel';

export function IntakeView({ caseId }: { caseId: string }) {
  const files = useFiles(caseId);
  const markDone = useMarkIntakeDone(caseId);

  const fileList = files.data ?? [];
  const fileCount = fileList.length;
  const stillProcessing = fileList.some(
    (f) =>
      f.ai_category === null ||
      f.extraction_status === 'pending' ||
      f.extraction_status === 'extracting',
  );
  const totalExtracted = fileList.reduce((n, f) => n + f.extracted_event_count, 0);

  const eventsLabel =
    totalExtracted === 1 ? m.intakeEventsOne() : m.intakeEventsMany({ count: totalExtracted });

  return (
    <div className="intake-view">
      <div className="intake-content">
        <header className="intake-head">
          <h1>{m.intakeHeading()}</h1>
          <p className="muted">{m.intakeIntro()}</p>
        </header>

        <FilesPanel caseId={caseId} />

        <div className="intake-status small muted">
          {fileCount === 0 ? (
            <span>{m.intakeNoFiles()}</span>
          ) : stillProcessing ? (
            <span>
              {fileCount === 1
                ? m.intakeStillProcessingOne()
                : m.intakeStillProcessingMany({ count: fileCount })}
            </span>
          ) : (
            <span>
              {fileCount === 1
                ? m.intakeProcessedOne({ events: eventsLabel })
                : m.intakeProcessedMany({ count: fileCount, events: eventsLabel })}
            </span>
          )}
        </div>

        <div className="intake-actions">
          <button
            type="button"
            className="btn-primary"
            disabled={markDone.isPending || (fileCount > 0 && stillProcessing)}
            onClick={() => markDone.mutate()}
            title={
              fileCount > 0 && stillProcessing ? m.intakeWaitTooltip() : m.intakeContinueTooltip()
            }
          >
            {fileCount === 0
              ? m.intakeSkip()
              : stillProcessing
                ? m.intakeWaiting()
                : m.intakeContinue()}
          </button>
        </div>
      </div>
    </div>
  );
}
